import { Injectable } from '@angular/core';
import { Crypto } from '@services/helpers/encryption-utils';
import { LocalStorageService } from '@services/helpers/local-storage.service';
import { BehaviorSubject, distinctUntilChanged } from 'rxjs';
import { ApiService } from './api.service';
import { HttpClient } from '@angular/common/http';
import { tap } from 'rxjs/operators';
import { StateCacheFetchService } from '@state-management/services/state-cache-fetch.service';
import { getTagSetting } from '@state-management/reducers';
import { FeatureFlagService } from '@services/helpers/feature-flag.service';

const ACTIVE_SUB_OFFICES_KEY = 'active_sub_offices';

@Injectable({
  providedIn: 'root',
})
export class SettingService {
  constructor(
    private api: ApiService,
    private http: HttpClient,
    private localStorageService: LocalStorageService,
    private cacheFetch: StateCacheFetchService,
    private featureFlagService: FeatureFlagService
  ) {}

  /**
   * Clears the tag-settings freshness stamp after a write, so the next forced `getTags()` goes to
   * the server instead of being served the pre-edit copy.
   *
   * `getTags()` serves a `force` fetch from cache for five minutes (ReduxService.CONFIG_FRESH_MS)
   * to stop the same 161 KB response being re-fetched on every module mount — see
   * FRONTEND-DUPLICATE-API-CALLS-2026-09-17.md §3. That window is only safe because every write
   * below cancels it: an author who edits a tag must never be shown the old list. Clears every
   * module_type rather than guessing which one a payload touched — the cost is one refetch, and a
   * missed invalidation is a stale-data bug.
   */
  private invalidateTagCache<T>() {
    return tap<T>(() => this.cacheFetch.invalidate(getTagSetting));
  }

  // GET API
  getGeneralSetting(type: string) {
    return this.api.get('/settings/visibility/' + this.api.getOfficeId() + '/' + type);
  }
  getEmailAccount() {
    return this.api.get('/settings/smtps?office_id=' + this.api.getOfficeId());
  }
  getEmailTemplate() {
    return this.api.get('/settings/email-template', { office_id: this.api.getOfficeId() });
  }
  setDefaultEmailTemplate(id: string) {
    return this.api.put('/settings/email-template-setdefault/' + id + '/' + this.api.getOfficeId());
  }

  // POST API

  createGeneralSetting(payload: any) {
    return this.api.post('/settings/visibility/' + this.api.getOfficeId(), payload);
  }
  createEmailAccount(payload: any) {
    return this.api.post('/settings/smtps', payload);
  }
  createEmailTemplate(payload: any) {
    return this.api.post('/settings/email-template', payload);
  }
  createLibraryFolderFile(payload: any) {
    return this.api.post('/library/add-folder-file', payload);
  }

  // PUT API
  updateEmailAccount(id: string, payload: any) {
    return this.api.put('/settings/smtps/' + id, payload);
  }
  updateEmailTemplate(id: string, payload: any) {
    return this.api.put('/settings/email-template/' + id, payload);
  }
  updateTagOrder(payload: any) {
    return this.api.put('/general/update-tags-order', payload);
  }
  updateColorStatus(payload: any) {
    return this.api.put('/offices/add-color/status', payload);
  }
  updateOfficeColor(payload: any) {
    return this.api.put('/offices/add-color/office', payload);
  }

  // DELETE API
  deleteReloadPageStatus() {
    return this.api.delete('/force-to-reload');
  }
  deleteEmailAccount(id) {
    return this.api.delete('/settings/smtps/' + id);
  }
  deleteEmailTemplate(id: string) {
    return this.api.delete('/settings/email-template/' + id);
  }

  //#region YPM Monitoring
  getYpmServerMonitoring(param: string) {
    return this.http.get(`https://newportalapi.ypmgroup.com.au/${param}`);
  }
  resetYpmServerMongoDB(param: string) {
    return this.http.post(`https://newportalapi.ypmgroup.com.au/${param}`, {});
  }

  // GET: https://newportalapi.ypmgroup.com.au/portal/metrics?from=&to=
  // Authorization:

  ypmPortalLogs(page, limit, start_date: string, end_date: string) {
    const token = 'e9f4d132b68a0c75f8b2e9d1457c30ad6e54a97f2b3d809c14f62e58d0b3a97c';
    return fetch(`https://newportalapi.ypmgroup.com.au/portal/api-activity-logs?page=${page}&limit=${limit}&from=${start_date}&to=${end_date}`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
    }).then(response => response.json());
  }

  //#endregion

  //#region Tag Manager

  // GET API
  resetTagManager() {
    return this.api.get('/general/tag-reset/' + this.api.getOfficeId()).pipe(this.invalidateTagCache());
  }
  getTagSettingList(payload: any) {
    return this.api.get('/general/tag-settings/', payload);
  }
  // POST API
  createTagModuleTagType(payload: any) {
    return this.api.post('/general/tag-settings/', payload).pipe(this.invalidateTagCache());
  }
  createTagAndCopyTag(payload: any) {
    return this.api.post('/general/tag-manager/', payload).pipe(this.invalidateTagCache());
  }
  createTagForApproval(payload: any) {
    return this.api.post('/general/request-tag/', payload);
  }
  // PUT API
  updateStatusOfTagManager(id: string, payload: any) {
    return this.api.put('/general/tag-manager/' + id, payload).pipe(this.invalidateTagCache());
  }
  updateTags(id: string, payload: any) {
    return this.api.put('/general/tag-manager/' + id, payload).pipe(this.invalidateTagCache());
  }
  updateTagModuleTagType(id: string, payload: any) {
    return this.api.put('/general/tag-settings/' + id, payload).pipe(this.invalidateTagCache());
  }
  updateTagModuleTagTypeOrder(id: string, payload: any) {
    return this.api.put('/general/tag-settings/' + id, payload).pipe(this.invalidateTagCache());
  }
  // DELETE API
  deleteTagModuleTagType(id: string, payload: any) {
    return this.api.delete('/general/tag-settings/' + id, payload).pipe(this.invalidateTagCache());
  }
  deleteTag(id: string) {
    return this.api.delete('/general/tag-manager/' + id).pipe(this.invalidateTagCache());
  }

  //#endregion

  //#region Tag Approval

  getRequestedTag(payload: any) {
    return this.api.get('/general/request-tag', payload);
  }
  approvedRequestedTag(id: string) {
    return this.api.post('/general/approve-tag/' + id, {});
  }
  removeRequestedTag(id: string) {
    return this.api.delete('/general/request-tag/' + id);
  }

  //#endregion

  //#region Library

  // GET API

  getFolderStructure() {
    return this.api.get(`/office-library/setting/get-folder-structure/${this.api.getUserId()}`);
  }
  getWaterMark() {
    return this.api.get(`/watermarks?office_id=${this.api.getOfficeId()}`);
  }
  getWaterMarkSetting() {
    return this.api.get('/watermarks/settings?office_id=' + this.api.getOfficeId());
  }

  // POST API

  createWaterMark(payload: any) {
    return this.api.post('/watermarks/settings', payload);
  }

  // PUT API
  updateFolderStructure(payload: any) {
    return this.api.put(`/office-library/setting/edit-folder-structure`, payload);
  }
  updateWaterMark(id: string, payload: any) {
    return this.api.put('/watermarks/rename/' + id, payload);
  }
  updateWaterMarkImage(id: string, payload: any) {
    return this.api.put(`/watermarks/${id}`, payload);
  }

  // DELETE API

  deleteWaterMark(id: string) {
    return this.api.delete('/watermarks/' + id);
  }

  // #endregion

  //#region sub offices (formerly portals)

  getSubOfficeList(params?: any) {
    return this.api.get('/sub-offices', params);
  }

  /**
   * Whether an empty Sub Office field may be filled with the first sub office on the add/edit
   * forms. Off unless the office is listed under `sub_office_auto_default` in
   * assets/feature-flags.json: the default put YPM Avanti on every record opened and saved,
   * which the client asked to stop.
   */
  isSubOfficeAutoDefaultEnabled(): boolean {
    return this.featureFlagService.isFeatureEnabledForOffice('sub_office_auto_default', this.api.getOfficeId());
  }
  addSubOffice(payload: any) {
    return this.api.post('/sub-offices', payload);
  }
  updateSubOffice(id: string, payload: any) {
    return this.api.put('/sub-offices/' + id, payload);
  }
  deleteSubOffice(id: string) {
    return this.api.delete('/sub-offices/' + id);
  }
  getSubOfficeById(id: string) {
    return this.api.get('/sub-offices/' + id);
  }

  /**
   * Sub offices that sit under the given main office(s) — used to narrow the sub office
   * picker on the user form when the main office selection changes.
   */
  getSubOfficesByOffices(officeIds: string[]) {
    const office_ids = officeIds?.length ? JSON.stringify(officeIds) : '[]';
    return this.api.get('/sub-offices/by-offices', { office_ids });
  }

  /**
   * Sub offices assigned to the logged-in user, each flagged with `is_selected`.
   * User is taken from the auth token — no id param.
   */
  getMySubOffices() {
    return this.api.get('/sub-offices/my');
  }

  /**
   * Saves which of the assigned sub offices the user currently has selected.
   * Mirrors how `active_office` works for the main office.
   */
  saveActiveSubOffices(payload: { user_id: string; active_sub_offices: string[] }) {
    return this.api.post('/users/active-sub-offices', payload);
  }

  /**
   * The header's sub office selection, shared with every list/grid that filters by it.
   *
   * Seeded from local storage (same trick `officeid` uses) so a page that boots before the
   * header has fetched `/sub-offices/my` still issues its first request with the right filter
   * instead of fetching once unfiltered and again a moment later.
   */
  private activeSubOffices = new BehaviorSubject<string[]>(this.readActiveSubOffices());

  private readActiveSubOffices(): string[] {
    try {
      const raw = Crypto.decrypt(this.localStorageService.GetItem(ACTIVE_SUB_OFFICES_KEY));
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  /** Current selection, for the first fetch a component makes. */
  getActiveSubOffices(): string[] {
    return this.activeSubOffices.value || [];
  }

  /** Emits on every change to the header selection. Skips no-op writes. */
  activeSubOffices$ = this.activeSubOffices.asObservable().pipe(distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)));

  setActiveSubOffices(ids: string[]) {
    const value = ids || [];
    this.localStorageService.SetItem(ACTIVE_SUB_OFFICES_KEY, Crypto.encrypt(JSON.stringify(value)));
    this.activeSubOffices.next(value);
  }

  /** `sub_office` query param the API expects: JSON array, or [] to mean "no filter". */
  activeSubOfficeParam(): any {
    const ids = this.getActiveSubOffices();
    return ids?.length > 0 ? JSON.stringify(ids) : [];
  }

  //#endregion

  //#region blocked leads (Time Wasters)

  /** Contacts (name + email) flagged so leads from them can be treated as time wasters. */
  getBlockedLeads() {
    return this.api.get('/blocked-leads', { office_id: this.api.getOfficeId() });
  }
  addBlockedLead(payload: { name: string; email: string }) {
    return this.api.post('/blocked-leads', { ...payload, office_id: this.api.getOfficeId() });
  }
  deleteBlockedLead(id: string) {
    return this.api.delete('/blocked-leads/' + id);
  }
  checkBlockedLead(email: string) {
    return this.api.get('/blocked-leads/check', { office_id: this.api.getOfficeId(), email });
  }
  getSpamReports(search?: string) {
    const params: any = { office_id: this.api.getOfficeId() };
    if (search) {
      params.search = search;
    }
    return this.api.get('/blocked-leads/spam-reports', params);
  }

  //#endregion

  //#endregion

  //#region Deletion Logs (audit trail) — master_admin only on the backend

  getDeletionLogs(params: any) {
    return this.api.get('/deletion-logs', params);
  }
  getDeletionLogById(id: string) {
    return this.api.get('/deletion-logs/' + id);
  }

  //#endregion

  //#region Exclude Followup (bulk clear follow-ups) — master admin only

  // Office staff, used to populate the "User" dropdown.
  getFollowUpStaffList() {
    return this.api.get('/users/byoffice', { office: this.api.getOfficeId(), show: 'staff' });
  }

  // payload: { office_id, user_ids, project_ids, from, to } — clears follow-up tasks (follow_up: true)
  // whose due_date falls in [from, to] for the selected users/projects.
  // user_ids / project_ids are arrays; empty array = no filter on that dimension.
  clearFollowUps(payload: any) {
    return this.api.post('/tasks/follow-up/bulk-clear', payload);
  }

  //#endregion
}
